import { useEffect, useRef, useState } from 'react';
import { Heart, MessageCircle, Play, Send, UserCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BrandMark } from '../components/AppShell';
import { EmptyState, LoadingRows } from '../components/StateViews';
import { fetchReels } from '../lib/firestore';
import { formatCompactCount } from '../lib/format';
import type { ReelClip } from '../types/content';

function ReelCard({ clip }: { clip: ReelClip }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void element.play().catch(() => undefined);
        } else {
          element.pause();
        }
      },
      { threshold: 0.7 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <article className="reel-card">
      <video ref={videoRef} src={clip.clipUrl} poster={clip.thumbnailUrl} muted playsInline loop preload="metadata" />
      <div className="reel-shade" />
      <header className="feed-brand">
        <BrandMark />
        <Link className="mini-login" to="/login">
          <UserCircle size={21} />
        </Link>
      </header>
      <div className="reel-copy">
        <p className="eyebrow">{clip.seriesTitle || 'InstaFlix Original'}</p>
        <h1>{clip.title}</h1>
        {clip.description && <p>{clip.description}</p>}
        <Link className="watch-pill" to={`/shows/${clip.seriesId}`}>
          <Play size={15} fill="currentColor" /> Show details
        </Link>
      </div>
      <aside className="reel-actions" aria-label="Reel actions">
        <span>
          <Heart size={24} />
          {formatCompactCount(clip.likeCount)}
        </span>
        <span>
          <MessageCircle size={24} />
          {formatCompactCount(clip.commentCount)}
        </span>
        <span>
          <Send size={24} />
          {formatCompactCount(clip.shareCount)}
        </span>
      </aside>
    </article>
  );
}

export function FeedPage() {
  const [clips, setClips] = useState<ReelClip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchReels()
      .then(setClips)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-pad"><LoadingRows label="Loading reels" /></div>;
  if (error) return <div className="page-pad"><EmptyState title="Feed unavailable" body="The reel feed could not be loaded right now." /></div>;
  if (clips.length === 0) return <div className="page-pad"><EmptyState title="No reels yet" body="Published studio episodes will appear here when they are ready." /></div>;

  return (
    <section className="feed-scroller" aria-label="Reel feed">
      {clips.map((clip) => (
        <ReelCard key={clip.id} clip={clip} />
      ))}
    </section>
  );
}
